import { Alert } from '@amore/ui/components/alert'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { ChevronRight, Inbox } from 'lucide-react'
import { daysBetween, plural, TODAY } from '@/lib/format'
import { dietLabels } from '@/lib/labels'
import type { WebReply, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { AttendanceDots } from './attendance'
import { partyTitle } from './model'

/**
 * "Odpovědi z webu": what guests sent through the wedding link. Nothing is
 * written into the guest list on its own; each reply waits under "Ke kontrole"
 * until the couple applies or dismisses it (2b). Handled ones collapse below.
 */
export function RepliesPage({
  data,
  onOpen,
  onShare,
}: {
  data: WeddingData
  onOpen: (reply: WebReply) => void
  onShare: () => void
}) {
  const newest = (a: WebReply, b: WebReply) => b.receivedAt.localeCompare(a.receivedAt)
  const waiting = data.webReplies.filter((reply) => reply.status === 'new').sort(newest)
  const handled = data.webReplies.filter((reply) => reply.status !== 'new').sort(newest)

  if (data.webReplies.length === 0)
    return (
      <EmptyState
        icon={<Inbox aria-hidden="true" />}
        title={
          data.website.published
            ? 'Zatím nikdo neodpověděl'
            : 'Odpovědi se tu objeví po zveřejnění webu'
        }
        description="Hosté odpovídají přes svatební odkaz. Každou odpověď tu zkontrolujete a jedním kliknutím zapíšete do seznamu."
        actions={<Button onClick={onShare}>Svatební odkaz</Button>}
      />
    )

  const groups: TableGroup<WebReply>[] = []
  if (waiting.length)
    groups.push({
      id: 'new',
      label: 'Ke kontrole',
      meta: replies(waiting.length),
      rows: waiting,
      collapsible: true,
    })
  if (handled.length)
    groups.push({
      id: 'handled',
      label: 'Vyřízené',
      meta: replies(handled.length),
      rows: handled,
      collapsible: true,
      defaultCollapsed: true,
    })

  return (
    <>
      {waiting.length === 0 ? (
        <Alert tone="success" announcement="none">
          Všechny odpovědi jsou vyřízené. Nové se objeví tady.
        </Alert>
      ) : (
        <p className="text-sm text-muted-foreground">
          Do seznamu hostů se odpovědi nezapisují samy. Otevřete odpověď, zkontrolujte ji a zapište.
        </p>
      )}
      <DataTable
        aria-label="Odpovědi z webu"
        columns={replyColumns(data)}
        getRowId={(reply) => reply.id}
        onRowClick={onOpen}
        rowMuted={(reply) => reply.status === 'dismissed'}
        groups={groups}
      />
    </>
  )
}

const replies = (count: number) => `${count} ${plural(count, 'odpověď', 'odpovědi', 'odpovědí')}`

function replyColumns(data: WeddingData): Column<WebReply>[] {
  return [
    {
      key: 'from',
      header: 'Host',
      width: 'minmax(0,1.4fr)',
      primary: true,
      cell: (reply) => {
        const party = data.parties.find((item) => item.id === reply.partyId)
        return (
          <span className="block min-w-0 space-y-0.5">
            <span className="block">{reply.name}</span>
            {party && partyTitle(party) !== reply.name ? (
              <span className="block truncate text-xs font-normal text-muted-foreground">
                za pozvánku {partyTitle(party)}
              </span>
            ) : null}
            {reply.message ? (
              <span className="block truncate text-xs font-normal text-muted-foreground italic">
                „{reply.message}“
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'answer',
      header: 'Odpověď',
      width: '5.5rem',
      interactive: true,
      cell: (reply) => (
        <AttendanceDots
          people={reply.answers.map((answer) => ({
            key: answer.personId,
            name: `${answer.firstName} ${answer.lastName}`.trim(),
            state: answer.attendance,
          }))}
        />
      ),
    },
    {
      key: 'details',
      header: 'Podrobnosti',
      width: 'minmax(0,1fr)',
      cell: (reply) => <span className="text-muted-foreground">{details(reply, data)}</span>,
    },
    {
      key: 'received',
      header: 'Datum',
      width: '6.5rem',
      mobile: 'hide',
      cell: (reply) => <span className="text-muted-foreground">{received(reply.receivedAt)}</span>,
    },
    {
      key: 'status',
      header: '',
      width: '7.5rem',
      align: 'end',
      cell: (reply) =>
        reply.status === 'new' ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-action">
            Zkontrolovat
            <ChevronRight aria-hidden="true" className="size-4" />
          </span>
        ) : (
          <Badge tone={reply.status === 'applied' ? 'success' : 'neutral'}>
            {reply.status === 'applied' ? 'Zapsáno' : 'Zamítnuto'}
          </Badge>
        ),
    },
  ]
}

/** The few things worth knowing before opening it: new names, needs, diets. */
function details(reply: WebReply, data: WeddingData) {
  const party = data.parties.find((item) => item.id === reply.partyId)
  const named = reply.answers.filter((answer) => {
    const person = party?.persons.find((item) => item.id === answer.personId)
    return person?.plusOne && !person.firstName && answer.firstName
  })
  const diets = [...new Set(reply.answers.flatMap((answer) => answer.diets))]
  const parts = [
    ...named.map((answer) => `doprovod ${answer.firstName} ${answer.lastName}`),
    reply.accommodation && 'ubytování',
    reply.transport && 'odvoz',
    ...diets.map((diet) => dietLabels[diet].toLowerCase()),
  ].filter(Boolean)
  return parts.join(' · ')
}

/** "dnes 19:12", "včera 8:40", "15. 10. 10:02". */
export function received(iso: string) {
  const [day = '', time = ''] = iso.split('T')
  const ago = daysBetween(day, TODAY)
  const clock = time.replace(/^0/, '')
  if (ago === 0) return `dnes ${clock}`
  if (ago === 1) return `včera ${clock}`
  const [, month, date] = day.split('-')
  return `${Number(date)}. ${Number(month)}. ${clock}`
}
